import type {Dispatch, ThunkAction, GetState} from 'src/reducers';

import type {
  JourneyExternalClickFiltersOptions,
  SavedDashboardFiltersListQuery,
  SavedDashboardFiltersListData,
  SavedDashboardFiltersList,
  Entity,
  FilterByID,
  MetricsType,
  SelectedFilters,
  JourneyOverviewDataTable,
  SelectedFilterColumnsData,
  CurrentJourneyBreadCrumbData,
  JourneyOverviewDataTableQuery,
  CurrentJourneyInsightData,
  SelectedDashboardEntityFilters,
} from 'src/types/analytics-genesis';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';
import * as reduxApi from 'src/utils/redux-api-v2';
import {thunkify as flow} from 'src/utils/thunks';
import {
  ANALYTICS_API_V3,
  ANALYTICS_API_V4,
} from 'src/utils/analytics-api-migration';
import {fetching, key} from 'src/utils/redux';
import {
  journeyClickInsightsTableColumnsQuery,
  urlClickInsightsTableColumnsQuery,
  transformEntities,
} from 'src/utils/analytics-genesis';
import {isEqual} from 'lodash';


export const RECEIVE_FILTERS_OPTIONS =
  'journey-click-insights/receive-filters-options';
export const RECEIVE_SAVED_DASHBOARDS =
  'journey-click-insights/receive-saved-dashboards';
export const SET_CURRENT_DASHBOARD =
  'journey-click-insights/set-current-dashboard';
export const SET_CURRENT_DASHBOARD_DRILLDOWN =
  'journey-click-insights/set-current-dashboard-drilldown';
export const SET_SELECTED_FILTERS =
  'journey-click-insights/set-selected-filters';
export const SET_SELECTED_ENTITIES =
  'journey-click-insights/set-selected-entities';
export const SET_SELECTED_ENTITIES_DRILLDOWN =
  'journey-click-insights/set-selected-entities-drilldown';
export const SET_SELECTED_JOURNEYS =
  'journey-click-insights/set-selected-journeys';
export const SET_SELECTED_DATE_RANGE_AND_FREQUENCY =
  'journey-click-insights/set-selected-date-range';
export const SET_SELECTED_METRICS_TYPE =
  'journey-click-insights/set-selected-metrics-type';
export const SET_CHART_FREQUENCY =
  'journey-click-insights/receive-chart-frequency';
export const RECEIVE_JOURNEY_OPTIONS =
  'journey-click-insights/receive-journey-options';

export const RECEIVE_JOURNEY_CLICK_INSIGHTS_TABLE_COLUMNS =
  'journey-click-insights/receive-journey-table-columns';
export const RECEIVE_TOUCHPOINT_TABLE_COLUMNS =
  'journey-click-insights/receive-touchpoint-table-columns';
export const RECEIVE_PEOPLE_TABLE_COLUMNS =
  'journey-click-insights/receive-people-table-columns';
export const SET_CURRENT_JOURNEY_DATA =
  'journey-click-insights/set-current-journey-data';

export const CLEAR_SELECTED_FILTERS = 'journey-click-insights/clear-filters';

export const SET_SELECTED_DASHBOARD_ENTITY_FILTERS =
  'journey-click-insights/set-selected-dashboard-entity-filters';
export const SET_SELECTED_JOURNEYS_DRILLDOWN =
  'journey-click-insights/set-selected-journeys-drilldown';
export const SET_SELECTED_FILTERS_DRILLDOWN =
  'journey-click-insights/set-selected-filters-drilldown';
export const SET_NAVIGATION_DOWN = 'journey-click-insights/set-navigation-down';
export const SET_NAVIGATION_UP = 'journey-click-insights/set-navigation-up';
export const SET_SELECTED_DATES = 'journey-click-insights/set-selected-dates';
export const SET_LOADING_DASHBOARD_FILTERS =
  'journey-click-insights/loading-dashboard-filters';

const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V4};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V4};

const analyticsApiV3Options: ApiPostOptions = {apiPath: ANALYTICS_API_V3};

type ReceiveFiltersOptionsAction = {
  type: typeof RECEIVE_FILTERS_OPTIONS;
  payload: JourneyExternalClickFiltersOptions;
};

type ReceiveSavedDashboardFilterListAction = {
  type: typeof RECEIVE_SAVED_DASHBOARDS;
  payload: {
    filters: SavedDashboardFiltersListData;
    dashboard: string;
    product: string;
  };
};

type SetCurrentDashboardAction = {
  type: typeof SET_CURRENT_DASHBOARD;
  payload: {
    dashboardKey: string;
    currentDashboard: SavedDashboardFiltersList | null;
  };
};

type SetCurrentDashboardDrilldownAction = {
  type: typeof SET_CURRENT_DASHBOARD_DRILLDOWN;
  payload: SavedDashboardFiltersList | null;
};

type SetSelectedFiltersAction = {
  type: typeof SET_SELECTED_FILTERS;
  payload: {
    dashboardKey: string;
    filters: SelectedFilters;
  };
};

type SetSelectedFiltersDrilldownAction = {
  type: typeof SET_SELECTED_FILTERS_DRILLDOWN;
  payload: SelectedFilters;
};

type SetSelectedJourneysDrilldownAction = {
  type: typeof SET_SELECTED_JOURNEYS_DRILLDOWN;
  payload: Array<FilterByID>;
};

type SetSelectedEntitiesAction = {
  type: typeof SET_SELECTED_ENTITIES;
  payload: {
    dashboardKey: string;
    entities: Array<Entity>;
  };
};

type SetSelectedEntitiesDrilldownAction = {
  type: typeof SET_SELECTED_ENTITIES_DRILLDOWN;
  payload: Array<Entity>;
};

type SetSelectedJourneysAction = {
  type: typeof SET_SELECTED_JOURNEYS;
  payload: {
    dashboardKey: string;
    journeys: Array<FilterByID>;
  };
};

type SetSelectedDateRangeAndFrequencyAction = {
  type: typeof SET_SELECTED_DATE_RANGE_AND_FREQUENCY;
  payload: DateRangeWithTimezone & {
    chartFrequency: string;
    dashboardKey: string;
  };
};

type SetSelectedMetricsTypeAction = {
  type: typeof SET_SELECTED_METRICS_TYPE;
  payload: MetricsType;
};

type SetCurrentJourneyDataAction = {
  type: typeof SET_CURRENT_JOURNEY_DATA;
  payload: CurrentJourneyBreadCrumbData;
};

type SetChartFrequencyAction = {
  type: typeof SET_CHART_FREQUENCY;
  payload: string;
};

type ReceiveJourneyOptionsAction = {
  type: typeof RECEIVE_JOURNEY_OPTIONS;
  payload: Array<FilterByID>;
};

type ReceiveJourneyTableColumnsAction = {
  type: typeof RECEIVE_JOURNEY_CLICK_INSIGHTS_TABLE_COLUMNS;
  payload: SelectedFilterColumnsData;
};

type ReceiveTouchpointTableColumnsAction = {
  type: typeof RECEIVE_TOUCHPOINT_TABLE_COLUMNS;
  payload: SelectedFilterColumnsData;
};

type ReceivePeopleTableColumnsAction = {
  type: typeof RECEIVE_PEOPLE_TABLE_COLUMNS;
  payload: SelectedFilterColumnsData;
};

type ReceiveSavedDashboardsAction = {
  type: typeof RECEIVE_SAVED_DASHBOARDS;
  payload: {
    filters: SavedDashboardFiltersListData;
    dashboard: string;
    product: string;
  };
};

type ClearSelectedFiltersAction = {
  type: typeof CLEAR_SELECTED_FILTERS;
  payload: {
    dashboardKey: string;
  };
};

type SetSelectedDashboardEntityFiltersAction = {
  type: typeof SET_SELECTED_DASHBOARD_ENTITY_FILTERS;
  payload: {
    dashboardKey: string;
  } & SelectedDashboardEntityFilters;
};

type SetNavigationDownAction = {
  type: typeof SET_NAVIGATION_DOWN;
  payload: boolean;
};

type SetNavigationUpAction = {
  type: typeof SET_NAVIGATION_UP;
  payload: boolean;
};

type SetSelectedDatesAction = {
  type: typeof SET_SELECTED_DATES;
  payload: {
    dashboardKey: string;
    dates: DateRangeWithTimezone;
  };
};

type SetLoadingDashboardFiltersAction = {
  type: typeof SET_LOADING_DASHBOARD_FILTERS;
  payload: boolean;
};

export type AnalyticsJourneysExternalClickInsightsActions =
  | ReceiveFiltersOptionsAction
  | SetSelectedFiltersAction
  | SetSelectedJourneysDrilldownAction
  | SetSelectedEntitiesAction
  | SetSelectedEntitiesDrilldownAction
  | SetSelectedJourneysAction
  | SetSelectedDateRangeAndFrequencyAction
  | SetSelectedMetricsTypeAction
  | SetChartFrequencyAction
  | ReceiveFiltersOptionsAction
  | ReceiveJourneyOptionsAction
  | SetCurrentJourneyDataAction
  | ReceiveJourneyTableColumnsAction
  | ReceiveTouchpointTableColumnsAction
  | ReceivePeopleTableColumnsAction
  | ReceiveSavedDashboardsAction
  | SetCurrentDashboardAction
  | SetCurrentDashboardDrilldownAction
  | ClearSelectedFiltersAction
  | SetSelectedDashboardEntityFiltersAction
  | SetSelectedFiltersDrilldownAction
  | SetSelectedJourneysDrilldownAction
  | SetNavigationDownAction
  | SetNavigationUpAction
  | SetSelectedDatesAction
  | SetLoadingDashboardFiltersAction;

const receiveFiltersOptions = (
  payload: JourneyExternalClickFiltersOptions,
): ReceiveFiltersOptionsAction => ({
  type: RECEIVE_FILTERS_OPTIONS,
  payload,
});

const receiveSavedDashboardFilterListData = (payload: {
  filters: SavedDashboardFiltersListData;
  dashboard: string;
  product: string;
}): ReceiveSavedDashboardFilterListAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload,
});

export const setCurrentDashboard = (
  dashboardKey: string,
  currentDashboard: SavedDashboardFiltersList | null,
): SetCurrentDashboardAction => ({
  type: SET_CURRENT_DASHBOARD,
  payload: {dashboardKey, currentDashboard},
});

export const setSelectedFilters = (
  dashboardKey: string,
  filters: SelectedFilters,
): SetSelectedFiltersAction => ({
  type: SET_SELECTED_FILTERS,
  payload: {dashboardKey, filters},
});

export const setSelectedEntities = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetSelectedEntitiesAction => ({
  type: SET_SELECTED_ENTITIES,
  payload: {dashboardKey, entities},
});

export const setSelectedJourneys = (
  dashboardKey: string,
  journeys: Array<FilterByID>,
): SetSelectedJourneysAction => ({
  type: SET_SELECTED_JOURNEYS,
  payload: {dashboardKey, journeys},
});

export const setSelectedDateRangeAndFrequency = (
  payload: DateRangeWithTimezone & {
    chartFrequency: string;
    dashboardKey: string;
  },
): SetSelectedDateRangeAndFrequencyAction => ({
  type: SET_SELECTED_DATE_RANGE_AND_FREQUENCY,
  payload,
});

export const setSelectedMetricsType = (
  payload: MetricsType,
): SetSelectedMetricsTypeAction => ({
  type: SET_SELECTED_METRICS_TYPE,
  payload,
});

export const setChartFrequency = (
  payload: string,
): SetChartFrequencyAction => ({
  type: SET_CHART_FREQUENCY,
  payload,
});

const receiveJourneyOptions = (
  payload: Array<FilterByID>,
): ReceiveJourneyOptionsAction => ({
  type: RECEIVE_JOURNEY_OPTIONS,
  payload,
});

const receiveJourneyClickInsightsTableColumnSelection = (
  payload: SelectedFilterColumnsData,
): ReceiveJourneyTableColumnsAction => ({
  type: RECEIVE_JOURNEY_CLICK_INSIGHTS_TABLE_COLUMNS,
  payload,
});

const receivePeopleTableColumnSelection = (
  payload: SelectedFilterColumnsData,
): ReceivePeopleTableColumnsAction => ({
  type: RECEIVE_PEOPLE_TABLE_COLUMNS,
  payload,
});

export const setCurrentJourneyData = (
  payload: CurrentJourneyBreadCrumbData,
): SetCurrentJourneyDataAction => ({
  type: SET_CURRENT_JOURNEY_DATA,
  payload,
});

export const clearSelectedFilters = (
  dashboardKey: string,
): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
  payload: {dashboardKey},
});

export const setSelectedDashboardEntityFilters = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetSelectedDashboardEntityFiltersAction => ({
  type: SET_SELECTED_DASHBOARD_ENTITY_FILTERS,
  payload: {dashboardKey, entities},
});

export const setNavigationDown = (
  isNavigationDown: boolean,
): SetNavigationDownAction => ({
  type: SET_NAVIGATION_DOWN,
  payload: isNavigationDown,
});

export const setNavigationUp = (
  isNavigationUp: boolean,
): SetNavigationUpAction => ({
  type: SET_NAVIGATION_UP,
  payload: isNavigationUp,
});

export const setSelectedDates = (
  dashboardKey: string,
  dates: DateRangeWithTimezone,
): SetSelectedDatesAction => ({
  type: SET_SELECTED_DATES,
  payload: {dashboardKey, dates},
});

export const setLoadingDashboardFilters = (
  payload: boolean,
): SetLoadingDashboardFiltersAction => ({
  type: SET_LOADING_DASHBOARD_FILTERS,
  payload,
});

export const getFiltersOptions: () => ThunkAction<unknown> = flow(
  key(() => `filtersOptions-journey-external-insights`),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  const allFilters = {
    filter_list: [
      'journey_lifecycle_goal',
      'journey_business_goal',
      'journey_category',
      'journey_status',
      'recipient_type',
      'journey_name',
      'event_name',
      'event_type',
    ],
  } as const;

  try {
    const response = await dispatch(
      reduxApi.post('/filters', allFilters, {}, analyticsApiV3Options),
    );
    dispatch(receiveFiltersOptions(response));
  } catch (error: any) {
    dispatch(
      receiveFiltersOptions({
        journey_lifecycle_goal: [],
        journey_business_goal: [],
        journey_category: [],
        journey_status: [],
        recipient_type: [],
        journey_name: [],
        event_name: [],
        event_type: [],
      }),
    );
    console.error('Error Fetching Filters Options:', error);
  }
});

export const getSavedJourneyInsightDashboards: (
  query: SavedDashboardFiltersListQuery,
) => ThunkAction<unknown> = flow(
  key(() => 'j2-analytics-deliverability-engagement-saved-dashboards'),
  fetching(),
)((query) => async (dispatch: Dispatch) => {
  try {
    const {
      filters,
    }: {
      filters: SavedDashboardFiltersListData;
    } = await dispatch(
      reduxApi.get(
        '/external-link-clicks/save-filter',
        query,
        analyticsOptions,
      ),
    );
    const defaultDashboard = filters.find(({is_default}) => is_default);
    dispatch(receiveSavedDashboardFilterListData({filters, ...query}));
    dispatch(
      setCurrentDashboard('insight_clicks_all', defaultDashboard ?? null),
    );
  } catch (error: any) {
    console.error('Error fetching saved dashboard filter:', error);
  }
});

export const getJourneyOptions: (
  query: string,
  limit: number,
) => ThunkAction<unknown> = flow(
  key((query, limit) => `journeyOptions-${query}-${limit}`),
  fetching(),
)((query, limit) => async (dispatch: Dispatch) => {
  const journeyFilter = {filter_list: ['journey_name'], query, limit} as const;
  try {
    const {journey_name} = await dispatch(
      reduxApi.post('/filters', journeyFilter, {}, analyticsApiV3Options),
    );
    dispatch(receiveJourneyOptions(journey_name));
  } catch (error: any) {
    console.error('Error Fetching Journey Options:', error);
    dispatch(receiveJourneyOptions([]));
  }
});

export const saveJourneyDashboard: (
  dashboardData: SavedDashboardFiltersList,
  dashboardKey: string,
) => ThunkAction<unknown> = flow(
  key(
    (dashboardData) =>
      `saveJourneyInsightDashboard-${JSON.stringify(dashboardData)}`,
  ),
  fetching(),
)(
  (dashboardData, dashboardKey) =>
    async (dispatch: Dispatch, getState: GetState) => {
      try {
        const {
          filters,
        }: {
          filters: SavedDashboardFiltersListData;
        } = await dispatch(
          reduxApi.post(
            '/external-link-clicks/save-filter',
            dashboardData,
            {},
            analyticsApiOptions,
          ),
        );
        const {name: dashboardName, product, dashboard, id} = dashboardData;
        dispatch(
          receiveSavedDashboardFilterListData({filters, product, dashboard}),
        );
        const {id: currentDashboardID} =
          getState().analyticsJourneysOverview.currentDashboard ?? {};
        //@ts-expect-error
        if (!(Boolean(id) && currentDashboardID !== id)) {
          const newDashboard = filters.find(({name}) => name === dashboardName);
          dispatch(setCurrentDashboard(dashboardKey, newDashboard ?? null));
        }
      } catch (error: any) {
        console.error('Error fetching saved dashboard filter:', error);
        throw error;
      }
    },
);

export const getSavedJourneyInsightDashboard: (
  query: SavedDashboardFiltersListQuery,
) => ThunkAction<unknown> = flow(
  key(() => 'journey-click-insights-saved-dashboards'),
  fetching(),
)((query) => async (dispatch: Dispatch) => {
  try {
    dispatch(setLoadingDashboardFilters(true));
    const {
      filters,
    }: {
      filters: SavedDashboardFiltersListData;
    } = await dispatch(
      reduxApi.get(
        '/external-link-clicks/save-filter',
        query,
        analyticsOptions,
      ),
    );
    const defaultDashboard = filters.find(({is_default}) => is_default);
    if (defaultDashboard) {
      const filters = defaultDashboard.filters ?? {};
      const {
        entity_data_filter = [{id: new Date().getTime()}],
        journey_lifecycle_goals = [],
        journey_business_goals = [],
        journey_categories = [],
        journey_status = [],
        recipient_type = [],
        journey_ids = [],
        event_types = [],
        from_address = [],
        selected_date_range = {
          startDateUTC: null,
          startDate: null,
          endDateUTC: null,
          endDate: null,
        },
      } = filters ?? {};
      const journeyFilters: SelectedFilters = {
        journey_lifecycle_goals,
        journey_business_goals,
        journey_categories,
        journey_status,
        recipient_type,
        journey_ids: journey_ids.map(({id}) => id),
        event_types,
        from_address,
      };
      dispatch(setSelectedEntities('insight_clicks_all', entity_data_filter));
      dispatch(setSelectedFilters('insight_clicks_all', journeyFilters));
      dispatch(
        setCurrentDashboard('insight_clicks_all', defaultDashboard ?? null),
      );
      dispatch(setSelectedDates('insight_clicks_all', selected_date_range));
    }
    dispatch(receiveSavedDashboardFilterListData({filters, ...query}));
  } catch (error: any) {
    console.error('Error fetching saved dashboard filter:', error);
  } finally {
    dispatch(setNavigationUp(false));
    dispatch(setNavigationDown(false));
    dispatch(setLoadingDashboardFilters(false));
  }
});

export const getSavedJourneyInsightDashboardDrilldown: (
  query: SavedDashboardFiltersListQuery,
) => ThunkAction<unknown> = flow(
  key(() => 'journey-click-insights-saved-dashboards-drilldown'),
  fetching(),
)((query) => async (dispatch: Dispatch, getState: GetState) => {
  try {
    dispatch(setLoadingDashboardFilters(true));
    const {
      filters,
    }: {
      filters: SavedDashboardFiltersListData;
    } = await dispatch(
      reduxApi.get(
        '/external-link-clicks/save-filter',
        query,
        analyticsOptions,
      ),
    );
    dispatch(receiveSavedDashboardFilterListData({filters, ...query}));
    const isNavigationDown =
      getState().analyticsJourneysClickInsightsOverview.isNavigationDown;
    const parentEntitiesFilters = getState()
      .analyticsJourneysClickInsightsOverview.selectedEntities[
      'insight_clicks_all'
    ] ?? [{id: new Date().getTime()}];
    const parentJourneyFilters =
      getState().analyticsJourneysClickInsightsOverview.selectedFilters[
        'insight_clicks_all'
      ] ?? {};

    const parentDateRange = getState().analyticsJourneysClickInsightsOverview
      .selectedDates['insight_clicks_all'] ?? {
      startDateUTC: null,
      startDate: null,
      endDateUTC: null,
      endDate: null,
    };
    const savedDashboardFilters =
      getState().analyticsJourneysClickInsightsOverview.saveDashboardFilterList[
        'insight_clicks_all'
      ] ?? [];

    const parentDefaultDashboard = savedDashboardFilters?.find(
      ({is_default}) => is_default,
    );

    const {filters: parentFilters = {}} = parentDefaultDashboard ?? {};

    const {
      entity_data_filter: entityFilters = [{id: new Date().getTime()}],
      selected_date_range = {
        startDateUTC: null,
        startDate: null,
        endDateUTC: null,
        endDate: null,
      },
      ...journeyFilters
    } = parentFilters ?? {};

    const loadSavedFilters = () => {
      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {
          // @ts-expect-error - TS2339 - Property 'entity_data_filter' does not exist on type 'SavedDashboardFiltersList'.
          entity_data_filter = [{id: new Date().getTime()}],
          // @ts-expect-error - TS2339 - Property 'journey_lifecycle_goals' does not exist on type 'SavedDashboardFiltersList'.
          journey_lifecycle_goals = [],
          // @ts-expect-error - TS2339 - Property 'journey_business_goals' does not exist on type 'SavedDashboardFiltersList'.
          journey_business_goals = [],
          // @ts-expect-error - TS2339 - Property 'journey_categories' does not exist on type 'SavedDashboardFiltersList'.
          journey_categories = [],
          // @ts-expect-error - TS2339 - Property 'journey_status' does not exist on type 'SavedDashboardFiltersList'.
          journey_status = [],
          // @ts-expect-error - TS2339 - Property 'recipient_type' does not exist on type 'SavedDashboardFiltersList'.
          recipient_type = [],
          // @ts-expect-error - TS2339 - Property 'journey_ids' does not exist on type 'SavedDashboardFiltersList'.
          journey_ids = [],
          // @ts-expect-error - TS2339 - Property 'event_types' does not exist on type 'SavedDashboardFiltersList'.
          event_types = [],
          // @ts-expect-error - TS2339 - Property 'from_address' does not exist on type 'SavedDashboardFiltersList'.
          from_address = [],
          // @ts-expect-error - TS2339 - Property 'selected_date_range' does not exist on type 'SavedDashboardFiltersList'.
          selected_date_range = {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        } = defaultDashboard ?? {};
        const journeyFilters: SelectedFilters = {
          journey_lifecycle_goals,
          journey_business_goals,
          journey_categories,
          journey_status,
          recipient_type,
          // @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type.
          journey_ids: journey_ids.map(({id}) => id),
          event_types,
          from_address,
        };
        dispatch(setSelectedEntities('insight_clicks_url', entity_data_filter));
        dispatch(setSelectedFilters('insight_clicks_url', journeyFilters));
        dispatch(setSelectedDates('insight_clicks_url', selected_date_range));
      }
      dispatch(
        setCurrentDashboard('insight_clicks_url', defaultDashboard ?? null),
      );
    };

    if (isNavigationDown === null) {
      loadSavedFilters();
      return;
    }
    if (isNavigationDown) {
      const isEntityFiltersChanged = !isEqual(
        transformEntities(entityFilters),
        transformEntities(parentEntitiesFilters),
      );

      const isJourneyFiltersChanged = !isEqual(
        journeyFilters,
        parentJourneyFilters,
      );

      const isDateRangeChanged = !isEqual(parentDateRange, selected_date_range);

      if (
        isEntityFiltersChanged ||
        isJourneyFiltersChanged ||
        isDateRangeChanged
      ) {
        dispatch(
          setSelectedEntities('insight_clicks_url', parentEntitiesFilters),
        );
        dispatch(
          setSelectedFilters('insight_clicks_url', parentJourneyFilters),
        );
        dispatch(setSelectedDates('insight_clicks_url', parentDateRange));
      } else {
        loadSavedFilters();
      }
      return;
    }
  } catch (error: any) {
    console.error('Error fetching saved dashboard filter:', error);
  } finally {
    dispatch(setNavigationUp(false));
    dispatch(setNavigationDown(false));
    dispatch(setLoadingDashboardFilters(false));
  }
});

export const getJourneyClickInsightTableFilterColumns: () => ThunkAction<unknown> =
  flow(
    key(() => 'getJourneyClickInsightTableFilterColumns'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedFilterColumnsData = await dispatch(
        reduxApi.get(
          '/external-link-clicks/column-selection',
          journeyClickInsightsTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveJourneyClickInsightsTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching journey column filter:', error);
    }
  });

export const saveSelectedFilterColumns: (
  columnData: SelectedFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) => `saveSelectedFiltersColumns-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/external-link-clicks/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getUrlInsightsTableFilterColumns: () => ThunkAction<unknown> =
  flow(
    key(() => 'getUrlInsightsTableFilterColumns'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedFilterColumnsData = await dispatch(
        reduxApi.get(
          '/external-link-clicks/column-selection',
          urlClickInsightsTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receivePeopleTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching url overview column filter:', error);
    }
  });

export const getJourneyURLLinkDetails: (
  sentLink: string,
) => ThunkAction<unknown> = flow(
  key((journeyId) => `getJourneyURLLinkDetails-${journeyId}`),
  fetching(),
)((sentLink) => async (dispatch: Dispatch) => {
  try {
    if (sentLink) {
      const response: {
        link: string;
        send_type: string;
      } = await await dispatch(
        reduxApi.get(
          '/external-link-clicks/sent-link-details',
          {sent_link: sentLink},
          analyticsOptions,
        ),
      );

      const journey: CurrentJourneyInsightData = {
        id: sentLink,
        name: response.link,
        sendType: response.send_type,
      };

      dispatch(setCurrentJourneyData({journey}));
    }
  } catch (error: any) {
    console.error('Error getting url insight', error);
  }
});
